export type MailingSystem = 'followup' | 'webinar' | 'plx';

export interface PlaceholderConfig {
  allowed: readonly string[];
  required?: string;
  system: MailingSystem;
  /**
   * Whether placeholder names are compared case-sensitively.
   * Must match the replacement regex in the corresponding service.
   * Default: true (webinar + PLX services use case-sensitive replacement).
   */
  caseSensitive?: boolean;
}

export const FOLLOWUP_PLACEHOLDERS = [
  'FOLLOWUP_LINK',
  'WEBINAR_TITLE',
  'WEBINAR_DATE',
  'EMAIL',
] as const;

export const WEBINAR_PLACEHOLDERS = [
  'WEBINAR_LINK',
  'WEBINAR_TITLE',
  'EMAIL',
  'WEBINAR_DATE_DYNAMIC',
  'WEBINAR_DATE_DYNAMIC_UPPER',
  'WEBINAR_DAY_WRITTEN',
  'WEBINAR_DATE:d.',
  'WEBINAR_DATE:d.m.',
  'WEBINAR_DATE:d.m.Y',
  'WEBINAR_DATE:Y',
  'WEBINAR_MONAT',
  'WEBINAR_TIME',
] as const;

export const PLX_PLACEHOLDERS = [
  'PRODUCT_LINK',
  'PRODUCT_NAME',
  'PRODUCT_CODE',
  'EMAIL',
  'DATE:d.m.Y',
  'DATE:d.m.',
  'DATE:Y',
] as const;

const PLACEHOLDER_REGEX_SOURCE = '(?<!\\{)\\{([A-Za-z][A-Za-z0-9_:.]*)\\}(?!\\})';

function escapeRegex(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

export function validatePlaceholders(
  htmlBody: string,
  subject: string,
  config: PlaceholderConfig,
): string[] {
  const issues: string[] = [];
  const caseSensitive = config.caseSensitive !== false;
  const normalize = (s: string) => (caseSensitive ? s : s.toLowerCase());
  const allowedSet = new Set(config.allowed.map(normalize));

  if (config.required) {
    const requiredRegex = new RegExp(
      `(?<!\\{)\\{${escapeRegex(config.required)}\\}(?!\\})`,
      caseSensitive ? '' : 'i',
    );
    if (!htmlBody || !requiredRegex.test(htmlBody)) {
      issues.push(`{${config.required}} fehlt im HTML-Inhalt`);
    }
  }

  const scan = (text: string, where: 'HTML' | 'Betreff') => {
    if (!text) return;
    const seen = new Set<string>();
    const regex = new RegExp(PLACEHOLDER_REGEX_SOURCE, 'g');
    let match: RegExpExecArray | null;
    while ((match = regex.exec(text)) !== null) {
      const name = match[1];
      if (allowedSet.has(normalize(name))) continue;
      if (seen.has(name)) continue;
      seen.add(name);
      issues.push(`Unbekannter Platzhalter im ${where}: {${name}}`);
    }
  };

  scan(htmlBody || '', 'HTML');
  scan(subject || '', 'Betreff');

  return issues;
}
